import { useReducer, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Page, State } from "./types";
import { AvailabilitySearchResponseToTimeSlotList } from "./types/TimeSlot";

import * as Mock from "./mocks";
import { defaultThemeStyles } from "./mocks";
import WidgetService from "./services/WidgetService";
import { CreateBookingStatus } from "./domain_objects/BookingResponse";
import { WidgetThemeStyles } from "./domain_objects/CrossSellSetupResponse";

// import DateHelper from "./helpers/DateHelper";
import WidgetConfiguratorMessageHandler from "./helpers/WidgetConfiguratorMessageHandler";
import SetupParamsHelper from "./helpers/SetupParamsHelper";
import AnalyticsHelper from "./helpers/AnalyticsHelper";

import { Button, Logo } from "./components";
import {
  SelectDatePage,
  SelectTimePage,
  PromotionPage,
  ContactDetailsPage,
  ConfirmDetailsPage,
  TermsAndConditionsPage,
  PrivacyPolicyPage,
  PaymentPage,
  BookingConfirmationPage,
  ErrorPage,
} from "./pages";
import DateHelper from "./helpers/DateHelper";

const StyledAppContainer = styled.div<{
  styles: WidgetThemeStyles;
  hostname: string;
}>`
  max-width: 360px;
  margin: 0 auto;
  background-color: ${({ styles }) => styles.OverallGeneralBackgroundColour};
  padding: 16px;
  border-radius: ${({ styles }) => `${styles.OverallGeneralBorderRadius}px`};
  color: ${({ styles }) => styles.OverallGeneralTextColour};

  font-family: ${({ styles }) => styles.OverallGeneralFont}, Roboto, system-ui !important;
  line-height: 1.5;
  font-weight: 400;

  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  @font-face {
    font-family: "Droid Sans";
    src: ${({ hostname }) =>
      `url("https://${hostname}/assets/fonts/DroidSans.ttf")`};
    font-weight: normal;
  }

  @font-face {
    font-family: "Droid Sans";
    src: ${({ hostname }) =>
      `url("https://${hostname}/assets/fonts/DroidSans-Bold.ttf")`};
    font-weight: bold;
  }
`;

const ReserveNowButtonContainer = styled(StyledAppContainer)`
  padding: 0;
  width: fit-content;
`;

const ReserveNowButton = styled(Button)`
  padding: 0 16px;
  margin-top: 10px;
`;

const setupParams = SetupParamsHelper.getSetupParams();

const initialState: State = {
  currentPage: "SelectDate",
  bookingDetails: {
    micrositeName: "",
    selectedDate: new Date(),
    partySize: 0,
  },
  initialTimeSlot: { TimeSlot: { TimeSlot: "" } },
  availabilityType: null,
  searchedCrossSellVenues: [],
  availableCrossSellVenues: [],
  flashBookingSummaryPanel: false,
} as State;

type Action =
  | { type: "NEXT_PAGE"; payload: Partial<State> }
  | { type: "PREV_PAGE"; payload: Partial<State> }
  | { type: "SET_STATE"; payload: Partial<State> }
  | { type: "SHOW_TERMS_AND_CONDITIONS"; payload: Partial<State> }
  | { type: "SHOW_PRIVACY_POLICY"; payload: Partial<State> };

const shouldShowPromotionsPage = (state: State): boolean => {
  if (state.bookingDetails.timeSlot?.TimeSlot.AvailablePromotions?.length) {
    return true;
  }
  return false;
};

const shouldShowPaymentPage = (state: State): boolean => {
  return (
    state.bookingConfirmationDetails?.Status ===
      CreateBookingStatus.PaymentRequired ||
    state.bookingConfirmationDetails?.Status ===
      CreateBookingStatus.CreditCardRequired ||
    state.bookingConfirmationDetails?.Status ===
      CreateBookingStatus.StripeAuthenticationRequired
  );
};

const shouldShowErrorPage = (state: State): boolean => {
  return (
    !shouldShowPaymentPage(state) &&
    state.bookingConfirmationDetails?.Status !== CreateBookingStatus.Success
  );
};

const VENUE_SEARCH_LIMIT = 2;

const getNextPageNewFlow = (state: State): Page => {
  switch (state.currentPage) {
    case "SelectDate":
      if (state.availabilityType === "full") {
        return shouldShowPromotionsPage(state)
          ? "PromotionPage"
          : "ContactDetails";
      } else {
        return "SelectTime";
      }
    case "SelectTime":
      if (shouldShowPromotionsPage(state)) {
        return "PromotionPage";
      } else {
        return "ContactDetails";
      }
    case "PromotionPage":
      return "ContactDetails";
    case "ContactDetails":
      return "ConfirmDetails";
    case "ConfirmDetails":
      if (shouldShowPaymentPage(state)) {
        return "PaymentPage";
      } else if (shouldShowErrorPage(state)) {
        return "ErrorPage";
      } else {
        return "BookingConfirmation";
      }
    case "PaymentPage":
      if (
        state.bookingConfirmationDetails?.Status === CreateBookingStatus.Success
      ) {
        return "BookingConfirmation";
      } else if (shouldShowErrorPage(state)) {
        return "ErrorPage";
      } else {
        return state.currentPage;
      }
    default:
      return state.currentPage;
  }
};

const getNextPageOldFlow = (state: State): Page => {
  switch (state.currentPage) {
    case "SelectDate":
      if (state.availabilityType === "full") {
        return shouldShowPromotionsPage(state)
          ? "PromotionPage"
          : "ContactDetails";
      } else {
        return "SelectTime";
      }
    case "SelectTime":
      if (shouldShowPromotionsPage(state)) {
        return "PromotionPage";
      } else {
        return "ContactDetails";
      }
    case "PromotionPage":
      return "ContactDetails";
    case "ContactDetails":
      return "ConfirmDetails";
    case "ConfirmDetails":
      if (shouldShowPaymentPage(state)) {
        return "PaymentPage";
      } else if (shouldShowErrorPage(state)) {
        return "ErrorPage";
      } else {
        return "BookingConfirmation";
      }
    case "PaymentPage":
      if (
        state.bookingConfirmationDetails?.Status === CreateBookingStatus.Success
      ) {
        return "BookingConfirmation";
      } else if (shouldShowErrorPage(state)) {
        return "ErrorPage";
      } else {
        return state.currentPage;
      }
    default:
      return state.currentPage;
  }
};

let getNextPage = (state: State): Page => {
  return getNextPageOldFlow(state);
};

const getPrevPageNewFlow = (state: State): Page => {
  switch (state.currentPage) {
    case "SelectTime":
      return "SelectDate";
    case "PromotionPage":
      return state.availabilityType === "full" ? "SelectDate" : "SelectTime";
    case "ContactDetails":
      if (shouldShowPromotionsPage(state)) {
        return "PromotionPage";
      } else {
        return state.availabilityType === "full" ? "SelectDate" : "SelectTime";
      }
    case "ConfirmDetails":
      return "ContactDetails";
    case "TermsAndConditions":
      return "ConfirmDetails";
    case "PrivacyPolicy":
      return "ConfirmDetails";
    case "PaymentPage":
      return "ConfirmDetails";
    case "ErrorPage":
      return shouldShowPaymentPage(state) ? "PaymentPage" : "ConfirmDetails";
    default:
      return state.currentPage;
  }
};

const getPrevPageOldFlow = (state: State): Page => {
  switch (state.currentPage) {
    case "SelectTime":
      return "SelectDate";
    case "PromotionPage":
      return state.availabilityType === "full" ? "SelectDate" : "SelectTime";
    case "ContactDetails":
      if (shouldShowPromotionsPage(state)) {
        return "PromotionPage";
      } else {
        return state.availabilityType === "full" ? "SelectDate" : "SelectTime";
      }
    case "ConfirmDetails":
      return "ContactDetails";
    case "TermsAndConditions":
      return "ConfirmDetails";
    case "PrivacyPolicy":
      return "ConfirmDetails";
    case "PaymentPage":
      return "ConfirmDetails";
    case "ErrorPage":
      return shouldShowPaymentPage(state) ? "PaymentPage" : "ConfirmDetails";
    default:
      return state.currentPage;
  }
};

let getPrevPage = (state: State): Page => {
  return getPrevPageOldFlow(state);
};

const reducer = (state: State, action: Action): State => {
  const updatedState = { ...state, ...action.payload };
  let result: State;
  switch (action.type) {
    case "NEXT_PAGE":
      result = {
        ...updatedState,
        currentPage: getNextPage(updatedState),
      };
      AnalyticsHelper.sendPageview(result);
      return result;
    case "PREV_PAGE":
      result = {
        ...updatedState,
        currentPage: getPrevPage(updatedState),
      };
      AnalyticsHelper.sendPageview(result);
      return result;
    case "SET_STATE":
      return {
        ...updatedState,
      };
    case "SHOW_TERMS_AND_CONDITIONS":
      result = {
        ...updatedState,
        currentPage: "TermsAndConditions",
      };
      AnalyticsHelper.sendPageview(result);
      return result;
    case "SHOW_PRIVACY_POLICY":
      result = {
        ...updatedState,
        currentPage: "PrivacyPolicy",
      };
      AnalyticsHelper.sendPageview(result);
      return result;
    default:
      return state;
  }
};

const App = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();

  useEffect(() => {
    if (
      !state.crossSellSetup?.UseNewSkipTimeSelectFlow &&
      !state.availabilityType
    ) {
      const baseAvailabilitySearch = state.availabilityResponses?.find(
        (a) => a.MicrositeName === state.micrositeName
      );
      if (baseAvailabilitySearch) {
        const timeslots = DateHelper.getTwoHourTimeSlots(
          AvailabilitySearchResponseToTimeSlotList(baseAvailabilitySearch),
          state.initialTimeSlot!
        );
        if (timeslots.length === 0) {
          dispatch({
            type: "SET_STATE",
            payload: {
              ...state,
              availabilityType: "partialOutsideTwoHours",
            },
          });
        }
        const slicedTimeSlots = timeslots.map((t) =>
          DateHelper.sliceTimeSlot(t)
        );
        const slicedInitialTimeSlot = DateHelper.sliceTimeSlot(
          state.initialTimeSlot!
        );
        if (
          timeslots.length > 0 &&
          !slicedTimeSlots.includes(slicedInitialTimeSlot)
        ) {
          dispatch({
            type: "SET_STATE",
            payload: {
              ...state,
              availabilityType: "partialWithinTwoHours",
            },
          });
        }
        if (
          timeslots.length > 0 &&
          slicedTimeSlots.includes(slicedInitialTimeSlot)
        ) {
          dispatch({
            type: "SET_STATE",
            payload: {
              ...state,
              availabilityType: "full",
            },
          });
        }
      }
    }
  }, [state]);

  useEffect(() => {
    const widgetService = new WidgetService();

    if (state.crossSellSetup === undefined) {
      const micrositeName = setupParams.MicrositeName;
      const channelCode = setupParams.ChannelCode;
      const themeId = setupParams.ThemeId;
      const hostname = setupParams.Hostname;

      widgetService
        .getWidgetSetup(
          hostname,
          micrositeName,
          new Date(),
          themeId,
          channelCode
        )
        .then((response) => {
          dispatch({
            type: "SET_STATE",
            payload: {
              ...state,
              crossSellSetup: {
                ...response,
                ThemeStyles: response.ThemeStyles || defaultThemeStyles,
                StripeInfo: response.StripeInfo,
              },
              hostname,
              micrositeName,
              channelCode: response.ThemeSettings?.ChannelCode || channelCode,
              themeId,
              initialTimeSlot: {
                TimeSlot: {
                  TimeSlot: response.UseNewSkipTimeSelectFlow
                    ? ""
                    : "1970-01-01T19:00:00",
                },
              },
            },
          });
          getNextPage = response.UseNewSkipTimeSelectFlow
            ? getNextPageNewFlow
            : getNextPageOldFlow;
          getPrevPage = response.UseNewSkipTimeSelectFlow
            ? getPrevPageNewFlow
            : getPrevPageOldFlow;
        });
    }
  }, [state]);

  const hasInitializedAnalytics = useRef(false);

  useEffect(() => {
    if (
      !hasInitializedAnalytics.current &&
      state.crossSellSetup?.TrackingSettings
    ) {
      AnalyticsHelper.initialiseAnalytics(state);
      hasInitializedAnalytics.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.crossSellSetup?.TrackingSettings]);

  const nextPage = (payload: Partial<State>) => {
    dispatch({ type: "NEXT_PAGE", payload });
  };

  const prevPage = (payload: Partial<State>) => {
    dispatch({ type: "PREV_PAGE", payload });
  };

  const { currentPage } = state;

  const openWidgetInNewTab = () => {
    window.open(
      `https://${state.hostname}/widget/CrossSell/${state.micrositeName}/${state.themeId}`,
      "_blank"
    );
  };

  return (
    state.crossSellSetup && (
      <>
        {window.location.protocol === "https:" && (
          <StyledAppContainer
            styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
            hostname={state.hostname || ""}
          >
            <WidgetConfiguratorMessageHandler
              state={state}
              setState={(payload: Partial<State>) =>
                dispatch({ type: "SET_STATE", payload })
              }
            />
            {currentPage === "SelectDate" && (
              <SelectDatePage
                nextPage={nextPage}
                state={state}
                timeSlotOptions={Mock.timeSlotOptions}
                widgetService={new WidgetService()}
              />
            )}
            {currentPage === "PromotionPage" && (
              <PromotionPage
                nextPage={nextPage}
                prevPage={prevPage}
                state={state}
              />
            )}
            {currentPage === "SelectTime" && (
              <SelectTimePage
                nextPage={nextPage}
                prevPage={prevPage}
                state={state}
                crossSellVenues={state.crossSellSetup?.CrossSellVenues}
                timeSlots={AvailabilitySearchResponseToTimeSlotList(
                  state.availabilityResponses!.find(
                    (a) => a.MicrositeName === state.micrositeName
                  )!
                )}
                widgetService={new WidgetService()}
                setState={(payload) =>
                  dispatch({
                    type: "SET_STATE",
                    payload: {
                      ...state,
                      ...payload,
                    },
                  })
                }
                venueSearchLimit={VENUE_SEARCH_LIMIT}
              />
            )}
            {currentPage === "ContactDetails" && (
              <ContactDetailsPage
                nextPage={nextPage}
                prevPage={prevPage}
                state={state}
              />
            )}
            {currentPage === "ConfirmDetails" && (
              <ConfirmDetailsPage
                nextPage={nextPage}
                prevPage={prevPage}
                state={state}
                widgetService={new WidgetService()}
                setState={(payload) =>
                  dispatch({
                    type: "SET_STATE",
                    payload: {
                      ...state,
                      ...payload,
                    },
                  })
                }
                showTermsAndConditionsPage={() =>
                  dispatch({ type: "SHOW_TERMS_AND_CONDITIONS", payload: {} })
                }
                showPrivacyPolicyPage={() =>
                  dispatch({ type: "SHOW_PRIVACY_POLICY", payload: {} })
                }
              />
            )}
            {currentPage === "PaymentPage" && (
              <PaymentPage
                state={state}
                nextPage={nextPage}
                prevPage={prevPage}
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
                widgetService={new WidgetService()}
              />
            )}
            {currentPage === "BookingConfirmation" && (
              <BookingConfirmationPage
                state={state}
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              />
            )}
            {currentPage === "TermsAndConditions" && (
              <TermsAndConditionsPage state={state} prevPage={prevPage} />
            )}
            {currentPage === "PrivacyPolicy" && (
              <PrivacyPolicyPage state={state} prevPage={prevPage} />
            )}
            {currentPage === "ErrorPage" && (
              <ErrorPage state={state} prevPage={prevPage} />
            )}
          </StyledAppContainer>
        )}
        {window.location.protocol !== "https:" && (
          <>
            <Logo
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
            />
            <ReserveNowButtonContainer
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              hostname={state.hostname || ""}
            >
              <ReserveNowButton
                styles={state.crossSellSetup.ThemeStyles || defaultThemeStyles}
                onClick={openWidgetInNewTab}
              >
                {t("Submit")}
              </ReserveNowButton>
            </ReserveNowButtonContainer>
          </>
        )}
      </>
    )
  );
};

export default App;
